<template>
  <section class="featured-carousel section">
    <div class="container">
      <!-- 标题区域 -->
      <div class="section-header">
        <span class="tips">博客</span>
        <h2 class="section-title">绿启篇章<span class="punctuation">，</span><br>奔赴代码的旷野<span class="punctuation">。</span></h2>
        <p class="section-subtitle">沉淀Java、Vue、云原生实战踩坑笔记</p>
      </div>

      <!-- 自由横向拖拽滚动容器 -->
      <div
          ref="scrollWrapRef"
          class="scroll-wrap"
          @mousedown="onMouseDown"
          @mousemove="onMouseMove"
          @mouseup="onMouseUp"
          @mouseleave="onMouseUp"
      >
        <div class="cards-row" :class="[{ 'justify-center': needCenter }]">
          <div v-for="project in featuredProjects" :key="project.id" class="card-wrapper">
            <LaputaBlogCard :blogCard="project"/>
          </div>
        </div>
      </div>
    </div>

    <!-- 背景装饰 -->
    <div class="bg-decoration">
      <div class="bg-blob bg-blob-1"></div>
      <div class="bg-blob bg-blob-2"></div>
    </div>
  </section>
</template>

<script setup>
import {ref,onMounted,onUnmounted,nextTick} from 'vue'
import LaputaBlogCard from "@/components/blog/LaputaBlogCard.vue";
import request from "@/api/request";
import showToast from "@/utils/toast";

const scrollWrapRef = ref(null)
// 是否需要居中
const needCenter = ref(false)

// 鼠标拖拽状态
let isDragging = false
let startX = 0
let scrollLeft = 0

const onMouseDown = (e)=>{
  isDragging = true
  startX = e.pageX
  scrollLeft = scrollWrapRef.value.scrollLeft
  scrollWrapRef.value.style.cursor = "grabbing"
}
const onMouseMove = (e)=>{
  if(!isDragging) return
  e.preventDefault()
  scrollWrapRef.value.scrollLeft = scrollLeft - (e.pageX - startX)
}
const onMouseUp = ()=>{
  if(!scrollWrapRef.value) return
  isDragging = false
  scrollWrapRef.value.style.cursor = "grab"
}

// 判断卡片总宽度是否小于容器可视宽度，决定居中与否
const calcLayout = () => {
  if(!scrollWrapRef.value) return
  const wrapDom = scrollWrapRef.value
  const rowDom = wrapDom.querySelector('.cards-row')
  if(!rowDom) return

  const wrapWidth = wrapDom.clientWidth
  const rowTotalWidth = rowDom.scrollWidth

  // 卡片总内容宽度小于容器可视宽度 → 居中
  needCenter.value = rowTotalWidth <= wrapWidth
}

const featuredProjects = ref([])
const queryHotBlogListLimit = async ()=>{
  try {
    const response = await request.post("/v1/api/queryHotBlogListLimit", {
      pageSize: 100
    })
    const resp = response.data
    if (resp.responseCode === '0000') {
      featuredProjects.value = resp.data
      await nextTick()
      calcLayout()
    }else {
      showToast(resp.responseCode)
    }
  } catch (e) {
    showToast(e.message)
  }
}

onMounted(async () => {
  await queryHotBlogListLimit()
  window.addEventListener('resize', calcLayout)
})
onUnmounted(()=>{
  window.removeEventListener('resize', calcLayout)
})
</script>

<style scoped>
span.punctuation {
  position: absolute;
}
.section-header .tips {
  color: var(--laputa-yellow);
  font-size: var(--laputa-fontsize-14);
}

.featured-carousel {
  position: relative;
  padding: 60px 0;
  min-height: fit-content;
  width: calc(100% - 20px);
  margin: 0;
  border-radius: 28px;
}

@media (max-width: 768px) {
  .featured-carousel {
    padding: 40px 0 32px 0;
    width: 100%;
    border-radius: 22px;
  }
}

@media (min-width: 2560px) {
  .featured-carousel {
    max-width: 1400px;
  }
}

.featured-carousel .container {
  max-width: 100%;
  margin: 0;
  padding: 0;
  width: 100%;
  box-sizing: border-box;
}

@media (min-width: 2560px) {
  .featured-carousel .container {
    padding: 0 36px;
  }
}

@media (max-width: 768px) {
  .featured-carousel .container {
    padding: 0;
  }
}

.bg-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  display: none;
}

.bg-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.4;
}

.bg-blob-1 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, rgba(100, 181, 246, 0.3), rgba(144, 202, 249, 0.15));
  top: -100px;
  left: -100px;
}

.bg-blob-2 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, rgba(100, 181, 246, 0.3), rgba(144, 202, 249, 0.15));
  bottom: -50px;
  right: -50px;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  z-index: 1;
}

.section-title {
  font-size: var(--laputa-fontsize-56);
  color: var(--laputa-green);
  font-weight: bold;
  text-align: center;
  line-height: 60px;
  margin-top: 12px;
  margin-bottom: 0;
}

@media (max-width: 1024px) {
  .section-title {
    font-size: 42px;
    line-height: 46px;
  }
}

@media (max-width: 768px) {
  .section-title {
    font-size: 2rem;
    line-height: 2.2rem;
  }
}

.section-subtitle {
  font-size: var(--laputa-fontsize-16);
  color: var(--laputa-common-sub-font-color);
  text-align: center;
  opacity: 0.8;
  margin: 24px 0 32px 0;
}

/* ==========核心横向滚动容器========== */
.scroll-wrap {
  width:100%;
  overflow-x: auto;
  overflow-y: hidden;
  cursor: grab;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  /* 新增：继承外层容器背景，缝隙就不会透出body底色 */
  background: transparent;
}
.scroll-wrap::-webkit-scrollbar {
  display: none;
}

.cards-row {
  display: flex;
  gap: 24px;
  padding: 8px 4px;
  flex-wrap: nowrap;
  justify-content: flex-start;
}
/* 卡片不足，开启居中 */
.cards-row.justify-center {
  justify-content: center;
}

.card-wrapper {
  flex-shrink: 0;
  flex-grow: 0;
  width: 340px;
}

@media (max-width: 1024px) {
  .card-wrapper {
    width: 300px;
  }
}
@media (max-width: 768px) {
  .card-wrapper {
    width: calc(100vw - 64px);
  }
}
</style>
